import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, LogOut, Settings } from 'lucide-react';

export type BackofficeUserMenuProps = {
  userLabel: string;
  onLogout: () => void;
};

export function BackofficeUserMenu({ userLabel, onLogout }: BackofficeUserMenuProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }

    function handlePointerDown(event: PointerEvent) {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }

      if (triggerRef.current?.contains(target) || menuRef.current?.contains(target)) {
        return;
      }

      setOpen(false);
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    }

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  return (
    <div className="relative min-w-0">
      <button
        ref={triggerRef}
        type="button"
        aria-label={`${userLabel} account menu`}
        aria-haspopup="menu"
        aria-expanded={open}
        className="focus-ring inline-flex max-w-full items-center gap-2 rounded-full border border-neutral-200 bg-white px-3 py-2 text-small font-medium text-neutral-700 transition hover:bg-backoffice-50"
        onClick={() => setOpen((current) => !current)}
      >
        <span className="truncate">{userLabel}</span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 transition ${open ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>

      {open ? (
        <div
          ref={menuRef}
          role="menu"
          className="absolute right-0 top-full z-20 mt-2 w-48 rounded-xl border border-neutral-200 bg-white p-1 shadow-lg"
        >
          <Link
            to="/settings"
            role="menuitem"
            className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-body font-medium text-neutral-700 transition hover:bg-backoffice-50"
            onClick={() => setOpen(false)}
          >
            <Settings className="h-4 w-4" aria-hidden="true" />
            <span>Settings</span>
          </Link>
          <button
            type="button"
            role="menuitem"
            className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-body font-medium text-neutral-700 transition hover:bg-backoffice-50"
            onClick={() => {
              setOpen(false);
              onLogout();
            }}
          >
            <LogOut className="h-4 w-4" aria-hidden="true" />
            <span>Log out</span>
          </button>
        </div>
      ) : null}
    </div>
  );
}
